import {
    CustomQuestionDto,
    MarketplaceAddressDto,
    MarketplaceDepositRuleDto,
    MarketplaceDto,
    SpeciesRequirementsDto,
    SpecialHourDto
} from 'actions/marketplace/MarketplaceDtos';
import moment from 'moment-timezone';
import { Option } from 'types/Generics';
import {
    IntakeFormCustomQuestion,
    IntakeFormCustomQuestionCategory,
    IntakeFormPreferences
} from 'types/IntakeForm';
import {
    Marketplace,
    MarketplaceAddress,
    MarketplaceDeposit,
    MarketplaceDepositApplicability,
    MarketplaceDepositCategory,
    MarketplaceDepositRule,
    MarketplacePetType,
    MarketplacePetTypes,
    MarketplaceServiceCategory,
    MarketplaceSettings,
    MarketplaceType,
    SpecialHour
} from 'types/Marketplace';
import { OnlineBookingPreferences } from 'types/OnlineBooking';
import { PetTypeId, PetTypeIds } from 'types/Pet';
import { parseJSON } from 'utils/json';

export const convertMarketplace = (marketplaceDto: MarketplaceDto): Marketplace => {
    return {
        id: marketplaceDto.id,
        slug: marketplaceDto.url_slug,
        name: marketplaceDto.business_name,
        logo: marketplaceDto.logo_url,
        phone: marketplaceDto.phone,
        timeZone: marketplaceDto.time_zone,
        businessType: convertToBusinessType(marketplaceDto),

        petTypes: convertToPetTypes(marketplaceDto),

        address:
            marketplaceDto.addresses.length > 0
                ? convertToMarketplaceAddress(marketplaceDto.addresses[0])
                : undefined,

        deposit: convertToDeposit(marketplaceDto),

        onlineBookingPreferences: convertToOnlineBookingPreferences(marketplaceDto),

        intakeFormPreferences: convertToIntakeFormPreferences(marketplaceDto),

        scheduleType: marketplaceDto.schedule_type as Marketplace['scheduleType'],
        schedule: parseJSON(marketplaceDto.range.range, {
            sunday: null,
            monday: null,
            tuesday: null,
            wednesday: null,
            thursday: null,
            friday: null,
            saturday: null
        }),

        specialHours: marketplaceDto.special_hours.map(specialHour =>
            convertToSpecialHour(specialHour)
        ),

        vaccinesByType: convertToVaccinesByType(marketplaceDto),

        // Pets
        catsEnabled: convertIsPetTypeEnabled(marketplaceDto.cats_detail ?? undefined),
        dogsEnabled: convertIsPetTypeEnabled(marketplaceDto.dogs_detail),
        exoticsEnabled: convertIsPetTypeEnabled(marketplaceDto.exotics_detail ?? undefined),

        settings: convertToMarketplaceSettings(marketplaceDto),

        evaluationEnabled: marketplaceDto.evaluation_enabled,

        gaTrackingId: marketplaceDto.ga_tracking_id,
        gtmTrackingId: marketplaceDto.gtm_tracking_id
    };
};

const parseTimeString = (time: string | false | null): { hour: number; minute: number } | null => {
    if (!time) return null;
    return {
        hour: parseInt(time.substring(0, 2), 10) || 0,
        minute: parseInt(time.substring(2, 4), 10) || 0
    };
};

const convertToMarketplaceSettings = (marketplaceDto: MarketplaceDto): MarketplaceSettings => {
    return {
        boardingEnabled: marketplaceDto.settings.boarding_enabled,
        daycareEnabled: marketplaceDto.settings.daycare_enabled,
        groomingEnabled: marketplaceDto.settings.grooming_enabled,
        mobileGroomingEnabled: marketplaceDto.settings.mobile_grooming_enabled,
        trainingEnabled: marketplaceDto.settings.training_enabled,
        walkingEnabled: marketplaceDto.settings.walking_enabled,
        leadsEnabled: marketplaceDto.settings.leads_enabled ?? false,

        morningFeedingEnabled: marketplaceDto.settings.morning_feeding_enabled,
        morningFeedingTime: parseTimeString(marketplaceDto.settings.morning_feeding_time),
        afternoonFeedingEnabled: marketplaceDto.settings.afternoon_feeding_enabled,
        afternoonFeedingTime: parseTimeString(marketplaceDto.settings.afternoon_feeding_time),
        eveningFeedingEnabled: marketplaceDto.settings.evening_feeding_enabled,
        eveningFeedingTime: parseTimeString(marketplaceDto.settings.evening_feeding_time),

        customDisclaimerEnabled: marketplaceDto.settings.custom_disclaimer_enabled ?? false,
        customDisclaimerMessage: marketplaceDto.settings.custom_disclaimer_message ?? '',

        requireVaccinesEnabled: marketplaceDto.settings.olb_require_vaccines_enabled ?? true,
        cardOnFileEnabled: marketplaceDto.settings.olb_card_on_file_enabled ?? false,
        intakeFormCardOnFileEnabled:
            marketplaceDto.settings.intake_form_card_on_file_enabled ?? false,

        waitlistEnabled: marketplaceDto.settings.olb_waitlist_enabled ?? false,

        brandPrimaryColor: marketplaceDto.settings.brand_primary_color ?? null,
        brandSecondaryColor: marketplaceDto.settings.brand_secondary_color ?? null
    };
};

const convertToBusinessType = (dto: MarketplaceDto) => {
    if (dto.mobile_business) {
        return MarketplaceType.MOBILE;
    } else if (dto.athome_business) {
        return MarketplaceType.ATHOME;
    } else if (dto.inhome_business) {
        return MarketplaceType.INHOME;
    }

    return MarketplaceType.LOCAL_BUSINESS;
};

const convertToMarketplaceAddress = (addresses: MarketplaceAddressDto): MarketplaceAddress => {
    return {
        id: addresses.id,
        addressLineOne: addresses.address_line_one,
        addressLineTwo: addresses.address_line_two,
        city: addresses.city,
        country: addresses.country,
        zipcode: addresses.zipcode,
        latitude: addresses.latitude,
        longitude: addresses.longitude,
        state: addresses.state
    };
};

const convertToDepositRule = (ruleDto: MarketplaceDepositRuleDto): MarketplaceDepositRule => ({
    categoryId: ruleDto.category_id,
    category: ruleDto.deposit_category as MarketplaceDepositCategory,
    value: Number(ruleDto.deposit_value)
});

const convertToDeposit = (marketplaceDto: MarketplaceDto): MarketplaceDeposit | undefined => {
    const deposit: MarketplaceDeposit = {
        category: marketplaceDto.deposit_category as MarketplaceDepositCategory,
        value: Number(marketplaceDto.deposit_value) || 0,
        applicability:
            marketplaceDto.deposit_applicability === MarketplaceDepositApplicability.SPECIFIC
                ? MarketplaceDepositApplicability.SPECIFIC
                : MarketplaceDepositApplicability.ALL,
        newClientsOnly: marketplaceDto.deposit_new_clients_only ?? false,
        rules: (marketplaceDto.deposit_rules ?? []).map(convertToDepositRule)
    };

    return isDepositEnabled(deposit) ? deposit : undefined;
};

/**
 * `deposit_category: "none"` switches deposits off in either mode. Otherwise
 * `all` needs a positive marketplace-level value, while `specific` needs at
 * least one per-category rule and ignores the marketplace-level value.
 */
export const isDepositEnabled = (deposit: MarketplaceDeposit): boolean => {
    if (deposit.category === MarketplaceDepositCategory.NONE) {
        return false;
    }

    if (deposit.applicability === MarketplaceDepositApplicability.SPECIFIC) {
        return deposit.rules.length > 0;
    }

    return (
        (deposit.category === MarketplaceDepositCategory.FIXED ||
            deposit.category === MarketplaceDepositCategory.PERCENTAGE) &&
        deposit.value > 0
    );
};

const convertToOnlineBookingPreferences = (
    marketplaceDto: MarketplaceDto
): OnlineBookingPreferences => {
    return {
        enabled: marketplaceDto.olb_enabled,
        allowNewClients: marketplaceDto.olb_accept_new_client,
        allowSelectStaff: marketplaceDto.olb_can_select_staff,
        bookingRequestCategories: {
            [MarketplaceServiceCategory.GROOMING]:
                marketplaceDto.settings.grooming_booking_request_enabled ?? false,
            [MarketplaceServiceCategory.DAYCARE]:
                marketplaceDto.settings.daycare_booking_request_enabled ?? false,
            [MarketplaceServiceCategory.BOARDING]:
                marketplaceDto.settings.boarding_booking_request_enabled ?? false,
            [MarketplaceServiceCategory.TRANSPORTATION]:
                marketplaceDto.settings.transportation_booking_request_enabled ?? false,
            [MarketplaceServiceCategory.WALKING]:
                marketplaceDto.settings.walking_booking_request_enabled ?? false,
            [MarketplaceServiceCategory.TRAINING]:
                marketplaceDto.settings.training_booking_request_enabled ?? false
        },
        promoImageUrl: marketplaceDto.promo_image_url,
        pricingOption: marketplaceDto.pricing_option as OnlineBookingPreferences['pricingOption'],
        bookingDateLimits: {
            from: marketplaceDto.olb_minimum_lead_time ?? null,
            to: marketplaceDto.olb_maximum_lead_time ?? null
        }
    };
};

const convertToIntakeFormPreferences = (marketplaceDto: MarketplaceDto): IntakeFormPreferences => {
    return {
        enabled: marketplaceDto.intake_form,
        questionsEnabled: marketplaceDto.custom_questions_enabled,
        questions: marketplaceDto.custom_questions.map(question =>
            convertToCustomQuestion(question, marketplaceDto)
        )
    };
};

const convertToSpecialHour = (dto: SpecialHourDto): SpecialHour => {
    return {
        id: dto.id,
        name: dto.name,
        closed: dto.closed,
        businessHours: parseJSON(dto.business_hours, {
            open: '',
            close: ''
        }),
        date: dto.date
    };
};

const NON_VACCINE_REQUIREMENTS = ['spay_and_neuter', 'manually_approve_vaccines'];

export const convertToVaccinesByType = (
    marketplaceDto: MarketplaceDto
): Record<PetTypeId, Array<Option<string>>> => {
    const fromSpecies = (species?: SpeciesRequirementsDto): Array<Option<string>> =>
        [...(species?.requirements ?? []), ...(species?.otherRequirements ?? [])]
            .filter(({ id }) => !NON_VACCINE_REQUIREMENTS.includes(id))
            .map(({ id, name }) => ({ id, name }));

    return {
        [PetTypeIds.DOG]: fromSpecies(marketplaceDto.requirements?.dogs),
        [PetTypeIds.CAT]: fromSpecies(marketplaceDto.requirements?.cats),
        [PetTypeIds.EXOTIC]: fromSpecies(marketplaceDto.requirements?.exotics)
    };
};

const convertIsPetTypeEnabled = (detail: string | undefined): boolean => {
    if ((detail ?? '').length === 0) {
        return false;
    } else {
        const petTypeDetail = JSON.parse(detail ?? '');
        return petTypeDetail.enabled ?? true;
    }
};

const convertToCustomQuestion = (
    dto: CustomQuestionDto,
    marketplace: MarketplaceDto
): IntakeFormCustomQuestion => {
    return {
        id: dto.id,
        isEnabled: dto.enabled,
        isRequired: dto.required,
        order: dto.display_order,
        question: dto.question_text,
        marketplaceId: dto.marketplace_id,
        category: dto.category as IntakeFormCustomQuestionCategory,
        createdAt: moment(dto.created_at).tz(marketplace.time_zone),
        updatedAt: moment(dto.updated_at).tz(marketplace.time_zone)
    };
};

const convertToPetType = (petType: string): MarketplacePetType => {
    const defaultValue = { enabled: false, sizes: [], ages: [], hairTypes: [], hairLengths: [] };

    return parseJSON(petType, defaultValue);
};

const convertToPetTypes = (marketplaceDto: MarketplaceDto): MarketplacePetTypes => {
    const cats: MarketplacePetType = convertToPetType(marketplaceDto.cats_detail ?? '');
    const dogs: MarketplacePetType = convertToPetType(marketplaceDto.dogs_detail);
    const exotics: MarketplacePetType = convertToPetType(marketplaceDto.exotics_detail ?? '');

    return {
        cats: {
            enabled: cats.enabled,
            sizes: cats.sizes ?? [],
            hairTypes: cats.hairTypes ?? [],
            hairLengths: cats.hairLengths ?? []
        },
        dogs: {
            enabled: dogs.enabled,
            sizes: dogs.sizes ?? [],
            hairTypes: dogs.hairTypes ?? [],
            hairLengths: dogs.hairLengths ?? []
        },
        exotics: {
            enabled: exotics.enabled,
            sizes: exotics.sizes ?? [],
            hairTypes: exotics.hairTypes ?? [],
            hairLengths: exotics.hairLengths ?? [],
            types: exotics.types ?? [],
            others: exotics.others ?? []
        }
    };
};
